import React, { useState, useEffect, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import TextField from '@mui/material/TextField';
import useAuth from '../../../../features/hooks/useAuth';
import { useSelector } from 'react-redux';
import { CommonButton } from '../../../../Styles/CommonStyles';
import { useGetUsersQuery } from '../../../../features/users/usersApiSlice';
import { useGetofficesQuery } from '../../../../features/offices/officesApiSlice';
import { useUpdateConsumableReqMutation } from '../../../../features/consumablerequest/consumableReqApiSlice';
import { useGetStoreOfficesQuery } from '../../../../features/storeoffices/storeofficesApiSlice';
import { useGetOfficeItemsQuery } from '../../../../features/officeitems/officeItemsApiSlice';
import nameStringFn from '../../../../data/nameStringFn';
import { useAlert } from '../../../ui/AlertProvider';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';

const StyledButton = styled(CommonButton)`
  padding: 8px 16px;
  min-width: 120px;
  margin-right: 10px;
`;

const ApproveButton = styled(StyledButton)`
  background-color: #4caf50;
`;

const ReturnButton = styled(StyledButton)`
  background-color: #ff9800;
`;

const StoreForwardButton = styled(StyledButton)`
  background-color: #2196f3;
`;

const RemarkContainer = styled.div`
  margin-top: 15px;
  width: 100%;
`;

const SelectedConsumablesContainer = styled.div`
  margin-top: 20px;
  width: 100%;
`;

const SelectedConsumablesTitle = styled.h3`
  margin-bottom: 10px;
  color: #1976d2;
`;

const StyledTableContainer = styled(TableContainer)`
  margin-bottom: 20px;
`;

const StyledTableHead = styled(TableHead)`
  background-color: #1976d2;
  
  & th {
    color: white;
    font-weight: bold;
  }
`;

const PartialNotice = styled.div`
  margin: 10px 0;
  padding: 10px;
  background-color: #fff3cd;
  border-left: 4px solid #ffc107;
  border-radius: 4px;
`;

const UnitHeadInboxFormCIR = ({ row, handleBack }) => {
  const [remark, setRemark] = useState('');
  const [isUpdatingReq, setIsUpdatingReq] = useState(false);
  
  const auth = useAuth();
  let user_current_office = useSelector((state) => state.office.off);
  const alert = useAlert();
  
  const { getSingleuser } = useGetUsersQuery('userslist', {
    selectFromResult: ({ data }) => ({
      getSingleuser: data?.entities
    }),
  });

  const { officedata } = useGetofficesQuery('offices', {
    selectFromResult: ({ data }) => ({
      officedata: data?.entities
    }),
  });
  
  const { storeoffices } = useGetStoreOfficesQuery('offices', {
    selectFromResult: ({ data }) => ({
      storeoffices: data?.entities
    }),
  });

  // Get BO office name to fetch their office items for available quantities
  const BO_office = useMemo(() => {
    if (!officedata) return user_current_office;
    const currentOfficeData = Object.values(officedata)?.find(office => office?.name === user_current_office);
    // For UnitHead, the BO is in the same office, so we use current office for inventory
    return user_current_office;
  }, [officedata, user_current_office]);

  // Fetch office items for BO's office to get available quantities
  const { officeItems } = useGetOfficeItemsQuery({ office_name: BO_office }, {
    selectFromResult: ({ data }) => ({
      officeItems: data?.entities
    }),
  });

  const [updateConsumableReq] = useUpdateConsumableReqMutation();
  
  // Get Store and BO IDs
  const storeOffice = Object.values(storeoffices || {})?.find(x => x.storeoffice === row?.req_store) || {};
  const storeIncharge_id = storeOffice?.roleUsers?.L1ReqApprover_C || "";
  const BO_id = officedata ? Object.values(officedata)?.find(office => office?.name === user_current_office)?.Bo : "";
  
  // Check if this is a partial issue case
  const isPartialIssue = row?.bo_partial_issue || false;
  
  // Check what kind of request this is
  const isPartialAndForward = row?.req_data?.[row.req_data.length - 1]?.sent_to_tag === 'boreq_parissue_forapprv';
  const isPartialAndClose = row?.req_data?.[row.req_data.length - 1]?.sent_to_tag === 'boreq_parissueclse_forapprv';
  const isFullIssue = row?.req_data?.[row.req_data.length - 1]?.sent_to_tag === 'boreq_issueallclose_forapprv';
  const isForwardToStore = row?.req_data?.[row.req_data.length - 1]?.sent_to_tag === 'boreq_fwd_unithead_store';
  const isBoForwardToStore = row?.req_data?.[row.req_data.length - 1]?.sent_to_tag === 'userreq_fwd_bo_to_uh_store_cir';
  const isReturnedFromStore = row?.req_data?.[row.req_data.length - 1]?.sent_to_tag === 'store_bts_return_unithead';
  
  // Check if this is an issued case (BO has issued consumables) vs just forwarded
  const isIssuedCase = isFullIssue || isPartialAndClose || isPartialAndForward;
  
  // Get the original requested consumables data (always from consumables_requested)
  const originalRequestedConsumables = row?.consumables_requested?.[row?.consumables_requested.length - 1]?.data || [];
  
  // Get the latest BO issued consumables data (if any)
  const latestBoIssuedConsumables = row?.bo_consum_issue?.length > 0 
    ? (row.bo_consum_issue[row.bo_consum_issue.length - 1]?.data || [])
    : [];
  
  // Display consumables data - use BO issued data directly if available to handle splits
  const latestConsumables = useMemo(() => {
    if (!originalRequestedConsumables.length) return [];
    
    // If BO has issued consumables (isIssuedCase), use those directly
    // This handles cases where BO splits one request into multiple part numbers
    if (isIssuedCase && latestBoIssuedConsumables.length > 0) {
      return latestBoIssuedConsumables.map(issuedItem => {
        // Find the original requested item to get the original quantity
        const originalItem = originalRequestedConsumables.find(req =>
          req.consumable === issuedItem.consumable &&
          req.asset === issuedItem.asset &&
          (req.assettag?.label === issuedItem.assettag?.label || req.assettag === issuedItem.assettag)
        );
        
        return {
          ...issuedItem,
          // Map to expected structure
          partno: issuedItem.partno,
          quantity: originalItem ? originalItem.quantity : issuedItem.quantity, // Original requested quantity
          issuedQuantity: issuedItem.quantity // The actual issued quantity
        };
      });
    }
    
    // For forward-to-store cases (no BO issue), show original requested data
    return originalRequestedConsumables.map(requestedItem => ({
      ...requestedItem,
      partno: requestedItem.partno || null,
      quantity: requestedItem.quantity,
      issuedQuantity: null
    }));
  }, [originalRequestedConsumables, latestBoIssuedConsumables, isIssuedCase]);

  // Debug logging
  console.log('UnitHead Data Debug:', {
    originalRequestedConsumables,
    latestBoIssuedConsumables,
    latestConsumables,
    isIssuedCase,
    isPartialIssue,
    mergedDataSample: latestConsumables.slice(0, 2).map(item => ({
      consumable: item.consumable,
      partno: item.partno,
      quantity: item.quantity,
      originalQuantity: item.originalQuantity,
      issuedQuantity: item.issuedQuantity
    }))
  });

  // Function to get issued quantity for a particular consumable item
  const getIssuedQuantity = useCallback((item) => {
    return item.issuedQuantity || 0;
  }, []);

  // Function to get available quantity for a particular partno - memoized
  const getAvailableQuantity = useCallback((partno) => {
    if (!officeItems || !partno) return 0;
    
    // Find the office inventory for BO's office
    const currentOfficeInventory = Object.values(officeItems).find(office => 
      office.office_name === BO_office
    );
    
    if (!currentOfficeInventory?.consumable_details) return 0;
    
    // Find the consumable item by partno
    const consumableItem = currentOfficeInventory.consumable_details.find(item => 
      item.partno === partno
    );
    
    return consumableItem ? parseInt(consumableItem.quantity || 0, 10) : 0;
  }, [officeItems, BO_office]);
  
  // Handle approval for user acceptance
  const handleApproveForUser = async () => {
    if (!remark.trim()) {
      alert.showWarning("Please provide a remark before approving.");
      return;
    }
    
    const requesterId = row?.req_creator_id;
    
    let sent_to_tag, action;
    
    if (isPartialAndClose) {
      sent_to_tag = "uhapprv_partissue_close";
      action = "Partial consumables issued and approved for user acceptance";
    } else if (isPartialAndForward) {
      sent_to_tag = "uhapprv_partissue_fwd";
      action = "Partial consumables issued and approved for user acceptance";
    } else {
      sent_to_tag = "uhapprv_fullissue_close";
      action = "All consumables issued and approved for user acceptance";
    }
    
    const senttoData = {
      sent_to_tag,
      Text: remark,
      sent_by: { 
        id: auth?.id, 
        name: getSingleuser?.[auth?.id]?.name, 
        office: user_current_office, 
        designation: auth?.designation?.[user_current_office] 
      },
      sent_to: { 
        id: requesterId, 
        name: getSingleuser?.[requesterId]?.name, 
        office: row?.req_data[0]?.sent_by?.office, 
        designation: getSingleuser?.[requesterId]?.designation?.[row?.req_data[0]?.sent_by?.office] 
      },
      action,
      curr_sent_to_string: `${nameStringFn(getSingleuser?.[requesterId])}-${row?.req_data[0]?.sent_by?.office}-${getSingleuser?.[requesterId]?.designation?.[row?.req_data[0]?.sent_by?.office] || 'Unknown'}`,
      time_stamp: new Date()
    };
    
    const body = { id: row?._id, senttoData: senttoData };
    
    try {
      setIsUpdatingReq(true);
      await updateConsumableReq(body).unwrap();
      alert.showSuccess("Request approved and sent to user for acceptance.", "Success", handleBack);
    } catch (error) {
      console.error("Error approving request:", error);
      alert.showError("There was an error approving the request. Please try again.");
    } finally {
      setIsUpdatingReq(false);
    }
  };
  
  // Handle forwarding to store
  const handleForwardToStore = async () => {
    if (!remark.trim()) {
      alert.showWarning("Please provide a remark before forwarding.");
      return;
    }
    
    if (!storeIncharge_id) {
      alert.showError("Store incharge information not found.");
      return;
    }
    
    const senttoData = {
      sent_to_tag: "userreq_uh_to_store_fwd_cir",
      Text: remark,
      sent_by: { 
        id: auth?.id, 
        name: getSingleuser?.[auth?.id]?.name, 
        office: user_current_office, 
        designation: auth?.designation?.[user_current_office] 
      },
      sent_to: { 
        id: storeIncharge_id, 
        name: getSingleuser?.[storeIncharge_id]?.name, 
        office: row?.req_store, 
        designation: getSingleuser?.[storeIncharge_id]?.designation?.[row?.req_store] 
      },
      action: "Request forwarded to store",
      curr_sent_to_string: `${nameStringFn(getSingleuser?.[storeIncharge_id])}-${row?.req_store}-${getSingleuser?.[storeIncharge_id]?.designation?.[row?.req_store] || 'Unknown'}`,
      time_stamp: new Date()
    };
    
    const body = { id: row?._id, senttoData: senttoData };
    
    try {
      setIsUpdatingReq(true);
      await updateConsumableReq(body).unwrap();
      alert.showSuccess("Request forwarded to store successfully.", "Success", handleBack);
    } catch (error) {
      console.error("Error forwarding request:", error);
      alert.showError("There was an error forwarding the request. Please try again.");
    } finally {
      setIsUpdatingReq(false);
    }
  };
  
  // Handle returning to BO for modification
  const handleReturnToBO = async () => {
    if (!remark.trim()) {
      alert.showWarning("Please provide a remark explaining what needs to be modified.");
      return;
    }

    const senttoData = {
      sent_to_tag: "userreq_uh_retrn_bo_mod_cir",
      Text: remark,
      sent_by: { 
        id: auth?.id, 
        name: getSingleuser?.[auth?.id]?.name, 
        office: user_current_office, 
        designation: auth?.designation?.[user_current_office] 
      },
      sent_to: { 
        id: BO_id, 
        name: getSingleuser?.[BO_id]?.name, 
        office: user_current_office, 
        designation: getSingleuser?.[BO_id]?.designation?.[user_current_office] 
      },
      curr_sent_to_string: `${nameStringFn(getSingleuser?.[BO_id])}-${user_current_office}-${getSingleuser?.[BO_id]?.designation?.[user_current_office] || 'Unknown'}`,
      time_stamp: new Date()
    };
    
    const body = { id: row?._id, senttoData: senttoData };
    
    try {
      setIsUpdatingReq(true);
      await updateConsumableReq(body).unwrap();
      alert.showSuccess("Request returned to BO for modification.", "Success", handleBack);
    } catch (error) {
      console.error("Error returning request:", error);
      alert.showError("There was an error returning the request. Please try again.");
    } finally {
      setIsUpdatingReq(false);
    }
  };

  // Handle resubmitting to store (when store returns request for modification)
  const handleResubmitToStore = async () => {
    if (!remark.trim()) {
      alert.showWarning("Please provide a remark before resubmitting to store.");
      return;
    }
    
    if (!storeIncharge_id) {
      alert.showError("Store incharge information not found.");
      return;
    }
    
    const senttoData = {
      sent_to_tag: "uh_resubmit_to_store_cir",
      Text: remark,
      sent_by: { 
        id: auth?.id, 
        name: getSingleuser?.[auth?.id]?.name, 
        office: user_current_office, 
        designation: auth?.designation?.[user_current_office] 
      },
      sent_to: { 
        id: storeIncharge_id, 
        name: getSingleuser?.[storeIncharge_id]?.name, 
        office: row?.req_store, 
        designation: getSingleuser?.[storeIncharge_id]?.designation?.[row?.req_store] 
      },
      action: "Request resubmitted to store by Unit Head",
      curr_sent_to_string: `${nameStringFn(getSingleuser?.[storeIncharge_id])}-${row?.req_store}-${getSingleuser?.[storeIncharge_id]?.designation?.[row?.req_store] || 'Unknown'}`,
      time_stamp: new Date()
    };
    
    const body = { id: row?._id, senttoData: senttoData };
    
    try {
      setIsUpdatingReq(true);
      await updateConsumableReq(body).unwrap();
      alert.showSuccess("Request resubmitted to store successfully.", "Success", handleBack);
    } catch (error) {
      console.error("Error resubmitting to store:", error);
      alert.showError("There was an error resubmitting to store. Please try again.");
    } finally {
      setIsUpdatingReq(false);
    }
  };

  return (
    <>
      <RemarkContainer>
        <TextField
          fullWidth
          multiline
          rows={4}
          label="Add Remark"
          variant="outlined"
          value={remark}
          onChange={(e) => setRemark(e.target.value)}
        />
      </RemarkContainer>

      {/* Display consumables in a table */}
      <SelectedConsumablesContainer>
        <SelectedConsumablesTitle>Requested Consumables</SelectedConsumablesTitle>
        
        {isPartialIssue && (
          <PartialNotice>
            <strong>Note:</strong> This is a partial issue request. Some items will be issued in quantities less than requested.
          </PartialNotice>
        )}

        <StyledTableContainer component={Paper}>
          <Table>
            <StyledTableHead>
              <TableRow>
                <TableCell>Sl.No.</TableCell>
                <TableCell>Consumable</TableCell>
                <TableCell>Asset</TableCell>
                <TableCell>Asset Tag</TableCell>
                <TableCell>Part No.</TableCell>
                {isIssuedCase && <TableCell>Available Qty</TableCell>}
                <TableCell>Requested Qty</TableCell>
                {isIssuedCase && <TableCell>To Be Issued</TableCell>}
              </TableRow>
            </StyledTableHead>
            <TableBody>
              {(latestConsumables || []).map((item, index) => (
                <TableRow key={index}>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>{item.consumable}</TableCell>
                  <TableCell>{item.asset}</TableCell>
                  <TableCell>{item.assettag.label}</TableCell>
                  <TableCell>{item.partno || '-'}</TableCell>
                  {isIssuedCase && <TableCell>{getAvailableQuantity(item.partno)}</TableCell>}
                  <TableCell>{item.modReqQuantity || item.quantity}</TableCell>
                  {isIssuedCase && <TableCell>{getIssuedQuantity(item)}</TableCell>}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </StyledTableContainer>
      </SelectedConsumablesContainer>

      <div style={{ marginTop: '20px', display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
        {/* When request is returned from Store to UH for resubmission */}
        {isReturnedFromStore && (
          <>
            <StoreForwardButton onClick={handleResubmitToStore} disabled={isUpdatingReq}>
              RESUBMIT TO STORE
            </StoreForwardButton>
            
            <ReturnButton onClick={handleReturnToBO} disabled={isUpdatingReq}>
              RETURN TO BO FOR MODIFICATION
            </ReturnButton>
          </>
        )}
        
        {/* When request is forwarded from BO to UH for store approval */}
        {isBoForwardToStore && !isReturnedFromStore && (
          <>
            <StoreForwardButton onClick={handleForwardToStore} disabled={isUpdatingReq}>
              FORWARD TO STORE
            </StoreForwardButton>
            
            <ReturnButton onClick={handleReturnToBO} disabled={isUpdatingReq}>
              RETURN TO BO FOR MODIFICATION
            </ReturnButton>
          </>
        )}
        
        {/* Original buttons for other cases */}
        {!isBoForwardToStore && !isReturnedFromStore && (
          <>
            {(isFullIssue || isPartialAndClose || isPartialAndForward) && (
              <ApproveButton onClick={handleApproveForUser} disabled={isUpdatingReq}>
                Approve and Send to User
              </ApproveButton>
            )}
            
            {/* Forward to Store button should NOT show for isPartialAndForward 
                because user must accept partial consumables first, 
                then it will auto-forward to store */}
            {(isForwardToStore && !isPartialAndForward) && (
              <StoreForwardButton onClick={handleForwardToStore} disabled={isUpdatingReq}>
                Forward to Store
              </StoreForwardButton>
            )}
            
            <ReturnButton onClick={handleReturnToBO} disabled={isUpdatingReq}>
              Return to BO for Modification
            </ReturnButton>
          </>
        )}
      </div>
    </>
  );
};

export default UnitHeadInboxFormCIR;